import React, { useRef, useState } from 'react';
import { Trans } from '@lingui/macro';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { IconClock, IconArrowRight } from '@tabler/icons-react';
import { Box, Flex, Text } from '~/design-system';
import Layout from '../Layout/Layout';
import Main from '../Layout/Main';
import PromptInput from './PromptInput';
import IntentChips from './IntentChips';
import BrowseAllPanel from './BrowseAllPanel';
import InProgressBanner from './InProgressBanner';
import MatchedCard from './MatchedCard';
import { useDescriptorSearch } from './useDescriptorSearch';

export default function SmartImportPicker() {
  const [query, setQuery] = useState('');
  const [browseOpen, setBrowseOpen] = useState(false);
  const browseRef = useRef<HTMLDivElement>(null);
  const { workspaceId } = useParams<{ workspaceId: string }>();

  const results = useDescriptorSearch(query);
  // Show up to 3 matches when the query yields results. Disambiguates short or
  // ambiguous queries (e.g. "pro" matches Profile and Product) instead of
  // forcing the user to trust a single top-1 pick.
  const matches = query.trim() ? results.slice(0, 3) : [];
  const topMatch = matches[0];

  function showBrowseAll() {
    setBrowseOpen(true);
    requestAnimationFrame(() => {
      browseRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  return (
    <Layout>
      <Main>
        <Box sx={{ maxWidth: 880, margin: '0 auto', width: '100%' }}>
          <Flex
            sx={{
              alignItems: 'center',
              justifyContent: 'flex-end',
              marginBottom: 2,
            }}
          >
            <Box
              as={RouterLink}
              // @ts-ignore — Box typing doesn't surface 'to' prop
              to={`/${workspaceId}/history/completed`}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                fontSize: '13px',
                color: '#6b7280',
                textDecoration: 'none',
                '&:hover': { color: '#2563eb' },
              }}
            >
              <IconClock size={14} stroke={1.75} />
              <Trans>View import history</Trans>
              <IconArrowRight size={14} stroke={2} />
            </Box>
          </Flex>

          <Box sx={{ marginBottom: 4 }}>
            <InProgressBanner />
          </Box>

          <Flex
            sx={{
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              marginBottom: 4,
            }}
          >
            <Box
              as="h1"
              sx={{
                fontSize: '28px',
                fontWeight: 700,
                color: '#111827',
                margin: 0,
                textAlign: 'center',
              }}
            >
              <Trans>
                Tell us what you're{' '}
                <Box as="span" sx={{ color: '#2563eb' }}>
                  importing
                </Box>
                .
              </Trans>
            </Box>
            <Text
              sx={{
                fontSize: '14px',
                color: '#6b7280',
                // @ts-ignore — theme-ui textAlign value
                textAlign: 'center',
                maxWidth: 540,
                marginTop: 1,
              }}
            >
              <Trans>
                Describe what you want to do in plain language, or drop your
                file into the box. We'll route you to the right importer — no
                jargon required.
              </Trans>
            </Text>
          </Flex>

          <Box sx={{ marginBottom: 3 }}>
            <PromptInput
              query={query}
              onQueryChange={setQuery}
              topMatch={topMatch}
            />
          </Box>

          <Box sx={{ marginBottom: 4 }}>
            <IntentChips onPick={setQuery} />
          </Box>

          {matches.length > 0 && (
            <Box sx={{ marginBottom: 4 }}>
              <MatchedCard matches={matches} onBrowseAll={showBrowseAll} />
            </Box>
          )}

          <Box ref={browseRef} sx={{ marginTop: 5 }}>
            <BrowseAllPanel open={browseOpen} onOpenChange={setBrowseOpen} />
          </Box>
        </Box>
      </Main>
    </Layout>
  );
}
